import { useState, useEffect, useRef } from "react";

const COLORS = {
  black: "#0a0a0a",
  gold: "#C9A84C",
  goldLight: "#E8C96A",
  goldDark: "#A07830",
  red: "#8B1A1A",
  cream: "#F5F0E8",
  charcoal: "#1E1E1E",
  gray: "#6B6B6B",
  lightGray: "#F0EDE8",
};

const style = (obj) => obj;

// ── NAV ──────────────────────────────────────────────────────────────────────
function Nav({ page, setPage }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const links = ["Home", "Our Story", "Timeline", "Membership", "Get Involved", "Contact"];

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      background: COLORS.black, borderBottom: `2px solid ${COLORS.gold}`,
      padding: "0 2rem", display: "flex", alignItems: "center",
      justifyContent: "space-between", height: "64px",
    }}>
      {/* Logo */}
      <div
        onClick={() => setPage("Home")}
        style={{ cursor: "pointer", display: "flex", flexDirection: "column", lineHeight: 1 }}
      >
        <span style={{ color: COLORS.gold, fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "1.2rem", letterSpacing: "0.12em" }}>AFRAM</span>
        <span style={{ color: COLORS.cream, fontFamily: "Georgia, serif", fontSize: "0.55rem", letterSpacing: "0.3em" }}>WESTERN REGION</span>
      </div>

      {/* Desktop links */}
      <div style={{ display: "flex", gap: "0.2rem", alignItems: "center" }} className="desktop-nav">
        {links.map(l => (
          <button key={l} onClick={() => setPage(l)} style={{
            background: page === l ? COLORS.gold : "transparent",
            color: page === l ? COLORS.black : COLORS.cream,
            border: "none", padding: "0.4rem 0.9rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.72rem", letterSpacing: "0.08em",
            borderRadius: "2px", transition: "all 0.2s",
          }}>{l.toUpperCase()}</button>
        ))}
        <button onClick={() => setPage("Membership")} style={{
          background: COLORS.gold, color: COLORS.black, border: "none",
          padding: "0.5rem 1.2rem", cursor: "pointer", fontFamily: "Georgia, serif",
          fontSize: "0.72rem", letterSpacing: "0.1em", fontWeight: 700,
          borderRadius: "2px", marginLeft: "0.5rem",
        }}>JOIN NOW</button>
      </div>

      {/* Mobile hamburger */}
      <button onClick={() => setMenuOpen(!menuOpen)} style={{
        display: "none", background: "transparent", border: "none",
        color: COLORS.gold, fontSize: "1.5rem", cursor: "pointer",
      }} className="hamburger">☰</button>

      {menuOpen && (
        <div style={{
          position: "fixed", top: "64px", left: 0, right: 0,
          background: COLORS.charcoal, padding: "1rem",
          display: "flex", flexDirection: "column", gap: "0.5rem",
        }}>
          {links.map(l => (
            <button key={l} onClick={() => { setPage(l); setMenuOpen(false); }} style={{
              background: "transparent", color: COLORS.cream, border: "none",
              padding: "0.7rem", cursor: "pointer", fontFamily: "Georgia, serif",
              fontSize: "0.9rem", textAlign: "left",
            }}>{l}</button>
          ))}
        </div>
      )}
    </nav>
  );
}

// ── HERO ─────────────────────────────────────────────────────────────────────
function HeroSection({ setPage }) {
  return (
    <section style={{
      minHeight: "100vh",
      background: `linear-gradient(160deg, ${COLORS.black} 0%, #1a0f02 50%, #0d0800 100%)`,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "6rem 2rem 4rem", position: "relative", overflow: "hidden",
    }}>
      {/* Decorative gold lines */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, bottom: 0, opacity: 0.07,
        backgroundImage: `repeating-linear-gradient(90deg, ${COLORS.gold} 0px, ${COLORS.gold} 1px, transparent 1px, transparent 80px)`,
        pointerEvents: "none",
      }} />

      <div style={{ textAlign: "center", maxWidth: "780px", position: "relative" }}>
        <div style={{
          display: "inline-block", border: `1px solid ${COLORS.gold}`,
          padding: "0.3rem 1.2rem", marginBottom: "1.5rem",
          fontFamily: "Georgia, serif", color: COLORS.gold,
          fontSize: "0.7rem", letterSpacing: "0.35em",
        }}>SEIU · AFRICAN AMERICAN CAUCUS</div>

        <h1 style={{
          fontFamily: "'Playfair Display', serif", color: COLORS.cream,
          fontSize: "clamp(2.8rem, 7vw, 5.5rem)", lineHeight: 1.05,
          fontWeight: 900, marginBottom: "0.5rem",
        }}>
          AFRAM<br />
          <span style={{ color: COLORS.gold }}>WEST</span>
        </h1>

        <p style={{
          fontFamily: "Georgia, serif", color: "#bbb", fontSize: "1.1rem",
          lineHeight: 1.7, marginBottom: "2.5rem", maxWidth: "560px", margin: "0 auto 2.5rem",
        }}>
          Empowering African American workers across the Western Region through solidarity, advocacy, and collective action.
        </p>

        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => setPage("Membership")} style={{
            background: COLORS.gold, color: COLORS.black, border: "none",
            padding: "0.9rem 2.5rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.9rem", fontWeight: 700,
            letterSpacing: "0.15em", borderRadius: "2px",
          }}>JOIN OUR COMMUNITY</button>
          <button onClick={() => setPage("Our Story")} style={{
            background: "transparent", color: COLORS.cream,
            border: `1px solid ${COLORS.gold}`, padding: "0.9rem 2.5rem",
            cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "0.9rem",
            letterSpacing: "0.1em", borderRadius: "2px",
          }}>OUR STORY</button>
        </div>
      </div>

      {/* Bottom stats bar */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        background: COLORS.gold, display: "flex", justifyContent: "center",
        gap: "4rem", padding: "1rem 2rem", flexWrap: "wrap",
      }}>
        {[["AZ · CA · NV · WA · OR", "Western States"], ["Est. 1985", "Founded"], ["12,000+", "Members"]].map(([num, label]) => (
          <div key={label} style={{ textAlign: "center" }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "1.1rem", color: COLORS.black }}>{num}</div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: "0.65rem", letterSpacing: "0.2em", color: "#333" }}>{label.toUpperCase()}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── MISSION ───────────────────────────────────────────────────────────────────
function MissionSection({ setPage }) {
  const pillars = [
    { icon: "✊", title: "Solidarity", desc: "Standing united with African American workers across every sector of the SEIU." },
    { icon: "⚖️", title: "Equity", desc: "Dismantling systemic racism and institutional disparities in the workplace and beyond." },
    { icon: "📣", title: "Advocacy", desc: "Amplifying Black voices in labor negotiations, policy, and community decisions." },
    { icon: "🌱", title: "Growth", desc: "Developing the next generation of African American labor leaders in the West." },
  ];

  return (
    <section style={{ background: COLORS.cream, padding: "5rem 2rem" }}>
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "3rem" }}>
          <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.7rem" }}>WHAT WE STAND FOR</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.black, fontSize: "2.5rem", fontWeight: 900 }}>Our Mission</h2>
          <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1rem auto 0" }} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "1.5rem" }}>
          {pillars.map(p => (
            <div key={p.title} style={{
              background: COLORS.black, padding: "2rem 1.5rem", borderRadius: "2px",
              borderTop: `3px solid ${COLORS.gold}`,
            }}>
              <div style={{ fontSize: "2rem", marginBottom: "0.8rem" }}>{p.icon}</div>
              <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.2rem", marginBottom: "0.5rem" }}>{p.title}</h3>
              <p style={{ fontFamily: "Georgia, serif", color: "#bbb", fontSize: "0.85rem", lineHeight: 1.65 }}>{p.desc}</p>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: "3rem" }}>
          <button onClick={() => setPage("Membership")} style={{
            background: COLORS.red, color: COLORS.cream, border: "none",
            padding: "0.85rem 2.5rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.85rem", letterSpacing: "0.12em", fontWeight: 700, borderRadius: "2px",
          }}>BECOME A MEMBER TODAY</button>
        </div>
      </div>
    </section>
  );
}

// ── HOME ──────────────────────────────────────────────────────────────────────
function HomePage({ setPage }) {
  return (
    <>
      <HeroSection setPage={setPage} />
      <MissionSection setPage={setPage} />

      {/* Quick CTA banner */}
      <section style={{
        background: COLORS.black, borderTop: `1px solid ${COLORS.goldDark}`,
        borderBottom: `1px solid ${COLORS.goldDark}`,
        padding: "3rem 2rem", textAlign: "center",
      }}>
        <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "2rem", marginBottom: "1rem" }}>
          Ready to Make a Difference?
        </h2>
        <p style={{ fontFamily: "Georgia, serif", color: "#aaa", marginBottom: "2rem", maxWidth: "500px", margin: "0 auto 2rem" }}>
          Join thousands of African American workers across the Western Region who are fighting for justice, equity, and dignity in the workplace.
        </p>
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => setPage("Membership")} style={{
            background: COLORS.gold, color: COLORS.black, border: "none",
            padding: "0.85rem 2rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.85rem", fontWeight: 700, letterSpacing: "0.12em", borderRadius: "2px",
          }}>JOIN NOW</button>
          <button onClick={() => setPage("Membership")} style={{
            background: "transparent", color: COLORS.gold, border: `1px solid ${COLORS.gold}`,
            padding: "0.85rem 2rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.85rem", letterSpacing: "0.12em", borderRadius: "2px",
          }}>RENEW MEMBERSHIP</button>
        </div>
      </section>

      {/* Leadership preview */}
      <section style={{ background: COLORS.lightGray, padding: "5rem 2rem" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "center" }}>
          <div style={{ color: COLORS.goldDark, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.7rem" }}>WESTERN REGION</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.black, fontSize: "2.2rem", fontWeight: 900, marginBottom: "0.5rem" }}>Our Leadership</h2>
          <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "0 auto 2.5rem" }} />

          <div style={{ display: "flex", justifyContent: "center", gap: "2rem", flexWrap: "wrap" }}>
            {[
              { name: "Ibrahim Coulibaly", role: "President – Western Region" },
              { name: "Western Region VP", role: "Vice President" },
              { name: "Western Region Sec.", role: "Secretary/Treasurer" },
            ].map(leader => (
              <div key={leader.name} style={{
                background: COLORS.black, color: COLORS.cream,
                padding: "2rem 1.5rem", borderRadius: "2px", width: "220px",
                borderBottom: `3px solid ${COLORS.gold}`,
              }}>
                <div style={{
                  width: "70px", height: "70px", borderRadius: "50%",
                  background: COLORS.gold, margin: "0 auto 1rem",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1.8rem",
                }}>✦</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1rem", marginBottom: "0.3rem", color: COLORS.gold }}>{leader.name}</div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: "0.75rem", color: "#aaa", letterSpacing: "0.05em" }}>{leader.role}</div>
              </div>
            ))}
          </div>
          <button onClick={() => setPage("Our Story")} style={{
            marginTop: "2rem", background: "transparent", color: COLORS.goldDark,
            border: `1px solid ${COLORS.goldDark}`, padding: "0.7rem 2rem",
            cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "0.8rem",
            letterSpacing: "0.1em", borderRadius: "2px",
          }}>LEARN MORE ABOUT US</button>
        </div>
      </section>
    </>
  );
}

// ── OUR STORY ─────────────────────────────────────────────────────────────────
function OurStoryPage({ setPage }) {
  return (
    <div style={{ paddingTop: "64px", background: COLORS.cream, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: COLORS.black, padding: "5rem 2rem 4rem",
        textAlign: "center", borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>SEIU AFRAM WESTERN REGION</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>Our Story</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0" }} />
      </div>

      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "4rem 2rem" }}>
        {[
          {
            heading: "Who We Are",
            body: "AFRAM West is the Western Region chapter of the SEIU African American Caucus — a powerful coalition of Black workers united within the Service Employees International Union. We represent African American SEIU members in California, Arizona, Nevada, Washington, Oregon, and across the Western states.",
          },
          {
            heading: "Our Purpose",
            body: "AFRAM West exists to amplify the voices of African American workers in SEIU, ensure equitable representation in union leadership, and combat systemic racism within both the workplace and the broader labor movement. We believe that racial justice and labor justice are inseparable.",
          },
          {
            heading: "Our People",
            body: "From healthcare workers and janitors to security officers and childcare providers, our members span every sector of the service economy. We are united by a common experience — and a common determination to build a more just and equitable world for ourselves, our families, and our communities.",
          },
          {
            heading: "Our Connection to SEIU AFRAM",
            body: "The Western Region operates as part of SEIU AFRAM's national structure, working alongside the Central, Eastern, and Southern Regions under the leadership of the National Executive Board. Our regional chapter focuses on the unique challenges and opportunities facing Black workers in the West.",
          },
        ].map((section, i) => (
          <div key={i} style={{ marginBottom: "3rem" }}>
            <h2 style={{
              fontFamily: "'Playfair Display', serif", color: COLORS.black, fontSize: "1.7rem",
              fontWeight: 900, marginBottom: "1rem", paddingLeft: "1rem",
              borderLeft: `4px solid ${COLORS.gold}`,
            }}>{section.heading}</h2>
            <p style={{ fontFamily: "Georgia, serif", color: "#333", fontSize: "1rem", lineHeight: 1.8 }}>{section.body}</p>
          </div>
        ))}

        <div style={{
          background: COLORS.black, padding: "2.5rem", borderRadius: "2px",
          borderLeft: `4px solid ${COLORS.gold}`, marginTop: "1rem",
        }}>
          <p style={{
            fontFamily: "'Playfair Display', serif", color: COLORS.cream,
            fontSize: "1.3rem", lineHeight: 1.7, fontStyle: "italic",
          }}>
            "At AFRAM, we are united in purpose and driven by a vision of justice and equity for all. While the challenges evolve, our resolve to confront and dismantle systemic racism grows stronger."
          </p>
        </div>

        <div style={{ textAlign: "center", marginTop: "3rem" }}>
          <button onClick={() => setPage("Timeline")} style={{
            background: COLORS.gold, color: COLORS.black, border: "none",
            padding: "0.85rem 2.5rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.85rem", fontWeight: 700, letterSpacing: "0.12em", borderRadius: "2px",
          }}>VIEW OUR HISTORY →</button>
        </div>
      </div>
    </div>
  );
}

// ── TIMELINE ──────────────────────────────────────────────────────────────────
function TimelinePage() {
  const events = [
    { year: "1968", title: "Roots of the Movement", desc: "African American SEIU members begin organizing informally to address racial inequities within the union and their workplaces. The civil rights era galvanizes Black workers across the Western states." },
    { year: "1972", title: "First Western Caucus Meetings", desc: "African American workers in California and neighboring states hold the first organized regional meetings, laying the groundwork for what will become AFRAM West." },
    { year: "1985", title: "AFRAM Founded", desc: "The SEIU African American Caucus (AFRAM) is officially founded nationally, with the Western Region among the first chapters established. Marchel Smiley emerges as a foundational leader of the movement." },
    { year: "1990", title: "Western Region Charter", desc: "AFRAM West receives its formal charter, establishing governance structures, bylaws, and a regional leadership council representing California, Nevada, Arizona, Oregon, and Washington." },
    { year: "1996", title: "Justice for Janitors Campaign", desc: "AFRAM West members play a pivotal role in the historic Justice for Janitors campaign in Los Angeles, winning landmark contracts for thousands of predominantly Black and brown service workers." },
    { year: "2001", title: "Healthcare Worker Expansion", desc: "Rapid growth in healthcare sector membership doubles AFRAM West's ranks. The region begins hosting annual leadership institutes to develop emerging Black union leaders." },
    { year: "2008", title: "Political Action Initiative", desc: "AFRAM West launches a major political action program, mobilizing African American SEIU members across the West in historic numbers during the presidential election cycle." },
    { year: "2012", title: "Fight for $15 Leadership", desc: "Western Region members spearhead SEIU's Fight for $15 campaign, with African American fast food and home care workers leading rallies from San Francisco to Las Vegas." },
    { year: "2015", title: "30th Anniversary Milestone", desc: "AFRAM celebrates its 30th anniversary. The Western Region reaches 10,000 active members and launches a new scholarship fund for members' children pursuing higher education." },
    { year: "2020", title: "Black Lives Matter & Labor", desc: "In the wake of the murder of George Floyd, AFRAM West leads SEIU in issuing a landmark racial justice action plan, connecting labor rights to the broader struggle for Black liberation." },
    { year: "2022", title: "Ibrahim Coulibaly Elected", desc: "Ibrahim Coulibaly is elected President of the Western Region, becoming one of the youngest regional presidents in AFRAM history. He prioritizes member engagement and digital organizing." },
    { year: "2024", title: "AFRAMWest Digital Launch", desc: "AFRAM West launches an expanded digital presence and online membership portal, making it easier than ever for African American SEIU members across the region to join, renew, and get involved." },
    { year: "2025", title: "Building the Future", desc: "AFRAM West continues its mission of solidarity, equity, and advocacy — growing stronger with each new member who joins the cause. The next chapter is being written by you." },
  ];

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, #0a0a0a 0%, #1a0f02 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>OUR LEGACY</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>History & Timeline</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "500px", margin: "0 auto" }}>
          Decades of struggle, solidarity, and progress — the story of African American workers in the Western Region.
        </p>
      </div>

      {/* Timeline */}
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "4rem 2rem" }}>
        <div style={{ position: "relative" }}>
          {/* Center line */}
          <div style={{
            position: "absolute", left: "50%", top: 0, bottom: 0,
            width: "2px", background: `linear-gradient(to bottom, ${COLORS.gold}, ${COLORS.goldDark})`,
            transform: "translateX(-50%)",
          }} />

          {events.map((event, i) => (
            <div key={i} style={{
              display: "flex",
              flexDirection: i % 2 === 0 ? "row" : "row-reverse",
              marginBottom: "2.5rem",
              alignItems: "flex-start",
              gap: "0",
            }}>
              {/* Content */}
              <div style={{
                width: "calc(50% - 30px)",
                background: COLORS.charcoal,
                padding: "1.5rem",
                borderRadius: "2px",
                borderTop: `2px solid ${COLORS.gold}`,
                marginRight: i % 2 === 0 ? "30px" : 0,
                marginLeft: i % 2 === 1 ? "30px" : 0,
              }}>
                <div style={{
                  fontFamily: "'Playfair Display', serif", color: COLORS.gold,
                  fontSize: "1.6rem", fontWeight: 900, marginBottom: "0.3rem",
                }}>{event.year}</div>
                <h3 style={{
                  fontFamily: "Georgia, serif", color: COLORS.cream,
                  fontSize: "0.95rem", fontWeight: 700, marginBottom: "0.6rem",
                  letterSpacing: "0.05em",
                }}>{event.title}</h3>
                <p style={{
                  fontFamily: "Georgia, serif", color: "#999",
                  fontSize: "0.82rem", lineHeight: 1.7,
                }}>{event.desc}</p>
              </div>

              {/* Center dot */}
              <div style={{
                width: "16px", height: "16px", borderRadius: "50%",
                background: COLORS.gold, border: `3px solid ${COLORS.black}`,
                flexShrink: 0, marginTop: "1.5rem",
                position: "relative", zIndex: 1,
              }} />

              {/* Spacer */}
              <div style={{ width: "calc(50% - 30px)" }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── MEMBERSHIP ────────────────────────────────────────────────────────────────
function MembershipPage() {
  const [formType, setFormType] = useState("join"); // "join" | "renew"
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    employer: "", local: "", memberID: "", state: "",
    joinDate: "", membershipType: "standard",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  const inputStyle = {
    width: "100%", background: "#111", border: `1px solid ${COLORS.goldDark}`,
    color: COLORS.cream, padding: "0.75rem 1rem", fontFamily: "Georgia, serif",
    fontSize: "0.9rem", borderRadius: "2px", boxSizing: "border-box",
    outline: "none",
  };

  const labelStyle = {
    fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.72rem",
    letterSpacing: "0.15em", display: "block", marginBottom: "0.4rem",
  };

  const tiers = [
    { id: "standard", name: "Standard Member", price: "$10/yr", perks: ["Monthly newsletter", "Regional event access", "Voting rights", "Member resources"] },
    { id: "sustaining", name: "Sustaining Member", price: "$25/yr", perks: ["All Standard benefits", "Leadership directory", "Priority event registration", "Digital resource library"] },
    { id: "founding", name: "Founding Patron", price: "$50/yr", perks: ["All Sustaining benefits", "Recognition in publications", "Steering committee access", "Legacy member status"] },
  ];

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.black} 0%, #1a0f00 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>JOIN THE MOVEMENT</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>Membership</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "500px", margin: "0 auto" }}>
          Become part of a powerful community of African American workers fighting for justice, equity, and dignity.
        </p>
      </div>

      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "4rem 2rem" }}>

        {/* Tier Selection */}
        <div style={{ marginBottom: "3rem" }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.8rem", marginBottom: "0.5rem", textAlign: "center" }}>Choose Your Membership</h2>
          <div style={{ width: "50px", height: "2px", background: COLORS.gold, margin: "0 auto 2rem" }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "1.2rem" }}>
            {tiers.map(t => (
              <div
                key={t.id}
                onClick={() => setFormData({ ...formData, membershipType: t.id })}
                style={{
                  background: formData.membershipType === t.id ? `linear-gradient(135deg, ${COLORS.goldDark}, ${COLORS.gold})` : COLORS.charcoal,
                  border: `2px solid ${formData.membershipType === t.id ? COLORS.gold : "#333"}`,
                  padding: "1.8rem 1.5rem", borderRadius: "2px", cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                <div style={{
                  fontFamily: "'Playfair Display', serif",
                  color: formData.membershipType === t.id ? COLORS.black : COLORS.gold,
                  fontSize: "1.2rem", fontWeight: 900, marginBottom: "0.3rem",
                }}>{t.name}</div>
                <div style={{
                  fontFamily: "Georgia, serif",
                  color: formData.membershipType === t.id ? COLORS.black : COLORS.cream,
                  fontSize: "1.5rem", fontWeight: 700, marginBottom: "1rem",
                }}>{t.price}</div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {t.perks.map(p => (
                    <li key={p} style={{
                      fontFamily: "Georgia, serif",
                      color: formData.membershipType === t.id ? "#222" : "#aaa",
                      fontSize: "0.8rem", lineHeight: 1.8,
                      paddingLeft: "1.2rem", position: "relative",
                    }}>
                      <span style={{ position: "absolute", left: 0, color: formData.membershipType === t.id ? COLORS.black : COLORS.gold }}>✦</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Form type toggle */}
        <div style={{ display: "flex", marginBottom: "2rem", borderBottom: `1px solid #333` }}>
          {["join", "renew"].map(type => (
            <button key={type} onClick={() => setFormType(type)} style={{
              flex: 1, padding: "1rem", background: "transparent",
              border: "none", borderBottom: formType === type ? `3px solid ${COLORS.gold}` : "3px solid transparent",
              color: formType === type ? COLORS.gold : "#666",
              fontFamily: "Georgia, serif", fontSize: "0.9rem", letterSpacing: "0.1em",
              cursor: "pointer", transition: "all 0.2s",
            }}>
              {type === "join" ? "NEW MEMBER SIGN-UP" : "RENEW MY MEMBERSHIP"}
            </button>
          ))}
        </div>

        {/* Form */}
        {!submitted ? (
          <div style={{ background: COLORS.charcoal, padding: "2.5rem", borderRadius: "2px", border: `1px solid #333` }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.4rem", marginBottom: "2rem" }}>
              {formType === "join" ? "Member Sign-Up Form" : "Membership Renewal Form"}
            </h3>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem", marginBottom: "1.2rem" }}>
              <div>
                <label style={labelStyle}>FIRST NAME *</label>
                <input style={inputStyle} name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First name" />
              </div>
              <div>
                <label style={labelStyle}>LAST NAME *</label>
                <input style={inputStyle} name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last name" />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem", marginBottom: "1.2rem" }}>
              <div>
                <label style={labelStyle}>EMAIL ADDRESS *</label>
                <input style={inputStyle} name="email" type="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" />
              </div>
              <div>
                <label style={labelStyle}>PHONE NUMBER</label>
                <input style={inputStyle} name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="(555) 555-5555" />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem", marginBottom: "1.2rem" }}>
              <div>
                <label style={labelStyle}>EMPLOYER / WORKPLACE *</label>
                <input style={inputStyle} name="employer" value={formData.employer} onChange={handleChange} placeholder="Your employer" />
              </div>
              <div>
                <label style={labelStyle}>SEIU LOCAL</label>
                <select style={{ ...inputStyle }} name="local" value={formData.local} onChange={handleChange}>
                  <option value="">Select your local</option>
                  <option>SEIU Local 6</option>
                  <option>SEIU Local 49</option>
                  <option>SEIU Local 87</option>
                  <option>SEIU Local 105</option>
                  <option>SEIU Local 121RN</option>
                  <option>SEIU Local 503</option>
                  <option>SEIU Local 521</option>
                  <option>SEIU Local 775</option>
                  <option>SEIU Local 1000</option>
                  <option>SEIU Local 2015</option>
                  <option>SEIU-UHW</option>
                  <option>Other</option>
                </select>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem", marginBottom: "1.2rem" }}>
              <div>
                <label style={labelStyle}>STATE</label>
                <select style={{ ...inputStyle }} name="state" value={formData.state} onChange={handleChange}>
                  <option value="">Select state</option>
                  <option>Arizona</option>
                  <option>California</option>
                  <option>Nevada</option>
                  <option>Oregon</option>
                  <option>Washington</option>
                  <option>Other Western State</option>
                </select>
              </div>
              {formType === "renew" && (
                <div>
                  <label style={labelStyle}>MEMBER ID (if known)</label>
                  <input style={inputStyle} name="memberID" value={formData.memberID} onChange={handleChange} placeholder="AFRAM-XXXX" />
                </div>
              )}
            </div>

            {formType === "renew" && (
              <div style={{ marginBottom: "1.2rem" }}>
                <label style={labelStyle}>ORIGINAL JOIN DATE (approximate)</label>
                <input style={inputStyle} name="joinDate" type="month" value={formData.joinDate} onChange={handleChange} />
              </div>
            )}

            <div style={{
              background: "#0d0d0d", padding: "1rem 1.2rem", borderRadius: "2px",
              marginBottom: "1.5rem", border: `1px solid #2a2a2a`,
            }}>
              <p style={{ fontFamily: "Georgia, serif", color: "#888", fontSize: "0.8rem", lineHeight: 1.6, margin: 0 }}>
                By submitting this form, you consent to receive communications from AFRAM West. Your membership dues support regional organizing, leadership development, and advocacy efforts on behalf of African American SEIU members in the Western Region.
              </p>
            </div>

            <button onClick={handleSubmit} style={{
              width: "100%", background: COLORS.gold, color: COLORS.black,
              border: "none", padding: "1.1rem", cursor: "pointer",
              fontFamily: "Georgia, serif", fontSize: "1rem", fontWeight: 700,
              letterSpacing: "0.15em", borderRadius: "2px",
            }}>
              {formType === "join" ? "SUBMIT APPLICATION & JOIN" : "SUBMIT RENEWAL"}
            </button>
          </div>
        ) : (
          <div style={{
            background: COLORS.charcoal, padding: "3rem 2rem", textAlign: "center",
            borderRadius: "2px", border: `2px solid ${COLORS.gold}`,
          }}>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✦</div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "2rem", marginBottom: "1rem" }}>
              {formType === "join" ? "Welcome to AFRAM West!" : "Membership Renewed!"}
            </h3>
            <p style={{ fontFamily: "Georgia, serif", color: COLORS.cream, fontSize: "1rem", lineHeight: 1.7, maxWidth: "500px", margin: "0 auto 2rem" }}>
              Thank you, {formData.firstName || "Member"}! Your {formType === "join" ? "application" : "renewal"} has been received. Our Western Region team will follow up with you at {formData.email || "your provided email"} within 2–3 business days.
            </p>
            <button onClick={() => { setSubmitted(false); setFormData({ firstName: "", lastName: "", email: "", phone: "", employer: "", local: "", memberID: "", state: "", joinDate: "", membershipType: "standard" }); }} style={{
              background: "transparent", color: COLORS.gold, border: `1px solid ${COLORS.gold}`,
              padding: "0.75rem 2rem", cursor: "pointer",
              fontFamily: "Georgia, serif", fontSize: "0.85rem", borderRadius: "2px",
            }}>SUBMIT ANOTHER FORM</button>
          </div>
        )}
      </div>

      {/* Benefits section */}
      <div style={{ background: COLORS.cream, padding: "4rem 2rem" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.black, fontSize: "2rem", textAlign: "center", marginBottom: "2.5rem" }}>Why Membership Matters</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.5rem" }}>
            {[
              { icon: "🗳️", title: "Your Voice", desc: "Shape the direction of the Western Region caucus and influence SEIU policy." },
              { icon: "📚", title: "Resources", desc: "Access leadership development programs, scholarships, and professional tools." },
              { icon: "🤝", title: "Network", desc: "Connect with thousands of African American workers across five Western states." },
              { icon: "⚡", title: "Power", desc: "Collective action is more effective when more members join the movement." },
            ].map(b => (
              <div key={b.title} style={{ background: COLORS.black, padding: "1.8rem", borderRadius: "2px", borderTop: `2px solid ${COLORS.gold}` }}>
                <div style={{ fontSize: "1.8rem", marginBottom: "0.7rem" }}>{b.icon}</div>
                <div style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.1rem", marginBottom: "0.5rem" }}>{b.title}</div>
                <p style={{ fontFamily: "Georgia, serif", color: "#bbb", fontSize: "0.83rem", lineHeight: 1.65 }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── GET INVOLVED ──────────────────────────────────────────────────────────────
function GetInvolvedPage({ setPage }) {
  const ways = [
    { icon: "📋", title: "Attend a Meeting", desc: "AFRAM West holds monthly regional meetings (virtual and in-person) across California, Arizona, Nevada, Oregon, and Washington. All members welcome." },
    { icon: "🎓", title: "Leadership Institute", desc: "Apply for our annual Leadership Development Institute — a weekend intensive designed to build the next generation of Black labor leaders in the West." },
    { icon: "📣", title: "Spread the Word", desc: "Recruit fellow African American SEIU members to join AFRAM West. Every new member strengthens our collective voice." },
    { icon: "💻", title: "Digital Organizing", desc: "Join our social media street team to amplify AFRAM West's message across platforms and reach members who haven't yet connected." },
    { icon: "🏛️", title: "Political Action", desc: "Volunteer for SEIU's political action campaigns and help elect leaders who will fight for African American workers." },
    { icon: "💛", title: "Donate", desc: "Support the mission with a financial contribution. Funds go directly toward organizing, scholarships, and regional programs." },
  ];

  return (
    <div style={{ paddingTop: "64px", background: COLORS.cream, minHeight: "100vh" }}>
      <div style={{
        background: COLORS.black, padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>TAKE ACTION</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>Get Involved</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "500px", margin: "0 auto" }}>
          There are many ways to contribute to the movement. Find your role.
        </p>
      </div>

      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "4rem 2rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.5rem" }}>
          {ways.map(w => (
            <div key={w.title} style={{
              background: COLORS.black, padding: "2rem", borderRadius: "2px",
              borderLeft: `3px solid ${COLORS.gold}`,
            }}>
              <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>{w.icon}</div>
              <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.15rem", marginBottom: "0.7rem" }}>{w.title}</h3>
              <p style={{ fontFamily: "Georgia, serif", color: "#bbb", fontSize: "0.85rem", lineHeight: 1.7 }}>{w.desc}</p>
            </div>
          ))}
        </div>

        <div style={{
          marginTop: "3rem", background: COLORS.black, padding: "3rem",
          borderRadius: "2px", textAlign: "center", borderTop: `3px solid ${COLORS.gold}`,
        }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.8rem", marginBottom: "1rem" }}>
            Start by Becoming a Member
          </h2>
          <p style={{ fontFamily: "Georgia, serif", color: "#aaa", marginBottom: "2rem" }}>
            Membership is the foundation of everything we do. Join AFRAM West today.
          </p>
          <button onClick={() => setPage("Membership")} style={{
            background: COLORS.gold, color: COLORS.black, border: "none",
            padding: "0.9rem 2.5rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.9rem", fontWeight: 700,
            letterSpacing: "0.12em", borderRadius: "2px",
          }}>JOIN AFRAM WEST →</button>
        </div>
      </div>
    </div>
  );
}

// ── CONTACT ────────────────────────────────────────────────────────────────────
function ContactPage() {
  const [msg, setMsg] = useState({ name: "", email: "", subject: "", body: "" });
  const [sent, setSent] = useState(false);

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.black} 0%, #1a0f00 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>REACH OUT</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>Contact Us</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0" }} />
      </div>

      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "4rem 2rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem" }}>
        {/* Info */}
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.5rem", marginBottom: "1.5rem" }}>AFRAM West</h2>
          {[
            { label: "Region", val: "Western Region" },
            { label: "States", val: "CA · AZ · NV · OR · WA" },
            { label: "National HQ", val: "SEIU AFRAM · P.O. Box 51700, Boston, MA 02205" },
            { label: "Email", val: "west@seiuafram.org" },
            { label: "Facebook", val: "facebook.com/groups/seiuafram" },
          ].map(item => (
            <div key={item.label} style={{ marginBottom: "1.2rem" }}>
              <div style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.2em", marginBottom: "0.2rem" }}>{item.label.toUpperCase()}</div>
              <div style={{ fontFamily: "Georgia, serif", color: COLORS.cream, fontSize: "0.9rem" }}>{item.val}</div>
            </div>
          ))}
        </div>

        {/* Form */}
        {!sent ? (
          <div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.4rem", marginBottom: "1.5rem" }}>Send a Message</h2>
            {[
              { label: "Your Name", key: "name", type: "text", placeholder: "Full name" },
              { label: "Email", key: "email", type: "email", placeholder: "your@email.com" },
              { label: "Subject", key: "subject", type: "text", placeholder: "Message subject" },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: "1rem" }}>
                <label style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.15em", display: "block", marginBottom: "0.4rem" }}>{f.label.toUpperCase()}</label>
                <input
                  style={{ width: "100%", background: "#111", border: `1px solid #333`, color: COLORS.cream, padding: "0.7rem 1rem", fontFamily: "Georgia, serif", fontSize: "0.9rem", borderRadius: "2px", boxSizing: "border-box" }}
                  type={f.type} placeholder={f.placeholder}
                  value={msg[f.key]} onChange={e => setMsg({ ...msg, [f.key]: e.target.value })}
                />
              </div>
            ))}
            <div style={{ marginBottom: "1.2rem" }}>
              <label style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.15em", display: "block", marginBottom: "0.4rem" }}>MESSAGE</label>
              <textarea
                style={{ width: "100%", background: "#111", border: `1px solid #333`, color: COLORS.cream, padding: "0.7rem 1rem", fontFamily: "Georgia, serif", fontSize: "0.9rem", borderRadius: "2px", boxSizing: "border-box", minHeight: "120px", resize: "vertical" }}
                placeholder="Your message..."
                value={msg.body} onChange={e => setMsg({ ...msg, body: e.target.value })}
              />
            </div>
            <button onClick={() => setSent(true)} style={{
              width: "100%", background: COLORS.gold, color: COLORS.black,
              border: "none", padding: "0.9rem", cursor: "pointer",
              fontFamily: "Georgia, serif", fontSize: "0.9rem", fontWeight: 700, letterSpacing: "0.12em", borderRadius: "2px",
            }}>SEND MESSAGE</button>
          </div>
        ) : (
          <div style={{ textAlign: "center", paddingTop: "2rem" }}>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✦</div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.6rem", marginBottom: "0.8rem" }}>Message Sent!</h3>
            <p style={{ fontFamily: "Georgia, serif", color: "#aaa", lineHeight: 1.7 }}>Thank you for reaching out. A member of the AFRAM West leadership team will be in touch soon.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── FOOTER ────────────────────────────────────────────────────────────────────
function Footer({ setPage }) {
  return (
    <footer style={{
      background: "#050505", borderTop: `2px solid ${COLORS.goldDark}`,
      padding: "3rem 2rem 1.5rem",
    }}>
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "2rem", marginBottom: "2rem" }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.5rem", fontWeight: 900, marginBottom: "0.3rem" }}>AFRAM WEST</div>
            <div style={{ fontFamily: "Georgia, serif", color: "#666", fontSize: "0.7rem", letterSpacing: "0.2em", marginBottom: "1rem" }}>SEIU AFRICAN AMERICAN CAUCUS</div>
            <p style={{ fontFamily: "Georgia, serif", color: "#777", fontSize: "0.82rem", lineHeight: 1.6 }}>Western Region — serving African American SEIU members in CA, AZ, NV, OR & WA.</p>
          </div>
          <div>
            <div style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.2em", marginBottom: "1rem" }}>NAVIGATION</div>
            {["Home", "Our Story", "Timeline", "Membership", "Get Involved", "Contact"].map(l => (
              <div key={l} style={{ marginBottom: "0.5rem" }}>
                <button onClick={() => setPage(l)} style={{ background: "transparent", border: "none", color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", cursor: "pointer", padding: 0 }}>{l}</button>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.2em", marginBottom: "1rem" }}>MEMBERSHIP</div>
            <button onClick={() => setPage("Membership")} style={{ background: "transparent", border: "none", color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", cursor: "pointer", display: "block", marginBottom: "0.5rem", padding: 0 }}>Join AFRAM West</button>
            <button onClick={() => setPage("Membership")} style={{ background: "transparent", border: "none", color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", cursor: "pointer", display: "block", marginBottom: "0.5rem", padding: 0 }}>Renew Membership</button>
            <button onClick={() => setPage("Get Involved")} style={{ background: "transparent", border: "none", color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", cursor: "pointer", display: "block", padding: 0 }}>Get Involved</button>
          </div>
        </div>
        <div style={{ borderTop: `1px solid #1a1a1a`, paddingTop: "1.5rem", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
          <div style={{ fontFamily: "Georgia, serif", color: "#555", fontSize: "0.75rem" }}>© 2025 SEIU AFRAM Western Region · A chapter of the SEIU African American Caucus</div>
          <div style={{ fontFamily: "Georgia, serif", color: "#555", fontSize: "0.75rem" }}>National HQ: P.O. Box 51700, Boston, MA 02205</div>
        </div>
      </div>
    </footer>
  );
}

// ── APP ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("Home");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const renderPage = () => {
    switch (page) {
      case "Home": return <HomePage setPage={setPage} />;
      case "Our Story": return <OurStoryPage setPage={setPage} />;
      case "Timeline": return <TimelinePage />;
      case "Membership": return <MembershipPage />;
      case "Get Involved": return <GetInvolvedPage setPage={setPage} />;
      case "Contact": return <ContactPage />;
      default: return <HomePage setPage={setPage} />;
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #0a0a0a; }
        input, select, textarea { outline: none; }
        input:focus, select:focus, textarea:focus { border-color: #C9A84C !important; }
        @media (max-width: 640px) {
          .desktop-nav { display: none !important; }
          .hamburger { display: block !important; }
        }
        @media (max-width: 640px) {
          [style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <Nav page={page} setPage={setPage} />
      <main>{renderPage()}</main>
      <Footer setPage={setPage} />
    </>
  );
}
